// Which kind of server answers this page: `all` (dev, test), `admin` (the organizer
// and App Admin host) or `event` (one event pinned by the server). Read once from
// `GET /api/version` and cached; an unreachable or older API means `all`.
import { api } from '@/api/client'
import type { AppRole, AppVersion } from '@/api/types'

export const DEFAULT_VERSION: AppVersion = { version: '', env: '', role: 'all', event_slug: null }

const ROLES: AppRole[] = ['all', 'admin', 'event']

export function normalizeVersion(v: Partial<AppVersion> | null | undefined): AppVersion {
  const role = ROLES.includes(v?.role as AppRole) ? (v!.role as AppRole) : 'all'
  return {
    version: typeof v?.version === 'string' ? v.version : '',
    env: typeof v?.env === 'string' ? v.env : '',
    role,
    event_slug: role === 'event' && typeof v?.event_slug === 'string' && v.event_slug ? v.event_slug : null,
  }
}

let cached: Promise<AppVersion> | null = null

/** Fetch `/api/version` once; later callers share the answer. A failed fetch is not cached. */
export function loadAppVersion(fetcher: () => Promise<Partial<AppVersion>> = () => api<AppVersion>('GET', '/version')): Promise<AppVersion> {
  if (!cached) {
    const p = fetcher().then(normalizeVersion)
    cached = p
    p.catch(() => {
      if (cached === p) cached = null
    })
  }
  return cached.catch(() => DEFAULT_VERSION)
}

export const loadAppRole = async (fetcher?: () => Promise<Partial<AppVersion>>): Promise<AppRole> => (await loadAppVersion(fetcher)).role

/** Tests (and a changed mock) start from a clean cache. */
export function resetAppRole() {
  cached = null
}

/** Organizer and App Admin exist on `all` and `admin` servers; an event server has neither. */
export const staffViewsAvailable = (role: AppRole) => role !== 'event'

/** The attendee event list and links belong on `all` only. */
export const showsAttendeeLinks = (role: AppRole) => role === 'all'

const STAFF_PREFIX = /^\/(organize|admin)(\/|$)/
/** True when this path must render NotFound on this server. */
export const blockedPath = (role: AppRole, path: string) => role === 'event' && STAFF_PREFIX.test(path)
